/* eslint-disable react/no-unused-state */
import React, { Component, Fragment } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import ReactHowler from "react-howler"
import ControlButton from "atoms/ControlButton"
import Timer from "atoms/Timer"
import ProgressBar from "atoms/ProgressBar"
import Thumbnail from "atoms/Thumbnail"
import LoadingOverlay from "molecules/LoadingOverlay"
import Header from "molecules/Header"
import ShareBox from "molecules/ShareBox"
import playerjs from "player.js"
import { getStyledSecondaryColor, getStyledPrimaryColor } from "theme/palette"
import screens from "theme/screens"

const DisabledOverlay = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  font-family: "Lato";
  font-size: 20px;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10000;
`

const PlayerWrapper = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  padding-left: ${({ leftPad }) => (leftPad ? "10px" : "0")}
  position: relative;
  width: 100%;
`

const InlineWrapper = styled.div`
  ${({ displayPlayer }) => (displayPlayer ? "" : "display: none;")}
  position: relative;
`

const fixedWrapperPaddingChooser = ({ variant }) => {
  if (variant === "minimal") {
    return "11px 21px"
  }

  if (variant === "small") {
    return "8px 21px 11px 21px"
  }

  if (variant === "mediumRight" || variant === "mediumLeft") {
    return "11px"
  }

  if (variant === "largeTop") {
    return "14px 11px 11px"
  }

  if (variant === "largeBottom") {
    return "19px 11px 11px"
  }

  return "11px 21px"
}

const borderChooser = ({ withBorder }) => withBorder ? "1px solid" : "initial"

const widthChooser = ({ variant }) => {
  if (variant === "minimal") {
    return "initial"
  }

  if (variant.indexOf("medium") === 0) {
    return `${screens.medium}px`
  }

  return `${screens.large}px`
}

const PlayerFixedWrapper = styled.div`
  ${({ displayPlayer }) => (displayPlayer ? "" : "display: none;")}
  background-color: ${getStyledSecondaryColor()};
  border: ${borderChooser};
  border-color: ${getStyledPrimaryColor("border")};
  border-radius: 8px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: ${fixedWrapperPaddingChooser};
  width: ${widthChooser};
  max-width: 100vw;
`

const MediumWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
`

const mediumPlayerWrapperMarginChooser = ({ variant }) => {
  if (variant === "left") {
    return "0 19px 0 30px"
  }

  if (variant === "right") {
    return "0 30px 0 19px"
  }

  return "initial"
}

const MediumPlayerWrapper = styled.div`
  margin: ${mediumPlayerWrapperMarginChooser};
  width: 100%;
`

const LargeWrapper = styled.div`
  align-items: center;
  display: flex;
  margin: ${({ variant }) => (variant === "top" ? "0 0 30px 0" : "25px 0 0")};
`

const TwoRowProgressWrapper = styled.div`
  align-items: center;
  display: flex;
`

class WebPlayer extends Component {
  constructor({ autoplay }) {
    super()
    this.state = {
      playState: autoplay ? "playing" : "stopped",
      loaded: false,
      loadingNewPage: false,
      currentTrackIdx: null,
      tracklist: [],
      publisher: {},
      trackPos: 0,
      duration: 0,
      page: 1,
      collapsed: true,
      isTwoRowPlayer: false,
      showShareOptions: false,
      fixedWrapperRef: null,
      inlineWrapperRef: null,
      wrapperSize: "large",
      playlistRequestOptions: {},
      didSessionStart: false,
    }

    this.playerWrapper = React.createRef()
    this.player = React.createRef()
    this.receiver = new playerjs.Receiver()
    this.ticking = false
  }

  componentDidMount() {
    this.loadStories(this.state.page)

    requestAnimationFrame(this.updateTrackPos)
    const { receiver } = this

    receiver.on("play", () => {
      this.play()
      receiver.emit("play")
    })

    receiver.on("pause", () => {
      this.pause()
      receiver.emit("pause")
    })

    receiver.on("getPaused", (callback) => callback(this.state.playState === "paused"))
    receiver.on("getDuration", (callback) => callback(this.state.duration))
    receiver.on("getCurrentTime", (callback) => callback(Math.round(this.state.trackPos)))
    receiver.on("setCurrentTime", (value) => this.handleProgressSeek((value / this.state.duration) * 100))
    receiver.ready()

    window.addEventListener("resize", this.onResize)
  }

  componentDidUpdate(prevProps, prevState) {
    const currentTrack = this.getCurrentTrack()

    if (
      currentTrack
      && prevState.didSessionStart === false
      && this.state.didSessionStart === true
    ) {
      this.props.analytics.trackSessionEvent("start")
    }

    // trigger onTrackChange callback
    if (this.props.onTrackChange) {
      if (this.state.currentTrackIdx !== prevState.currentTrackIdx) {
        this.props.onTrackChange(currentTrack)
      }
    }

    // trigger onAudioStart callback
    if (this.props.onAudioStart) {
      if (this.state.playState === "playing") {
        if (
          (prevState.playState === "stopped") // first play and plays after skip forwards
          || (this.state.currentTrackIdx !== prevState.currentTrackIdx) // track changes
        ) {
          this.props.onAudioStart(currentTrack)
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize)
  }

  onResize = () => {
    if (!this.ticking) {
      requestAnimationFrame(this.handleOnResize)
    }
    this.ticking = true
  }

  setCurrentScreenVariant = (width) => {
    if (width >= screens.large) {
      this.setState({
        isTwoRowPlayer: false,
        wrapperSize: "large",
      })
    }

    if (width >= screens.small && width < screens.large) {
      this.setState({
        isTwoRowPlayer: false,
        wrapperSize: "small",
      })
    }

    if (width >= screens.minimal && width < screens.small) {
      this.setState({
        isTwoRowPlayer: true,
        wrapperSize: "minimal",
      })
    }

    if (width < screens.minimal) {
      this.setState({
        isTwoRowPlayer: true,
        wrapperSize: "extraMinimal",
      })
    }
  }

  getCurrentTrack = () => this.state.tracklist[this.state.currentTrackIdx]

  handleOnResize = () => {
    this.ticking = false
    if (this.state.fixedWrapperRef) {
      this.setCurrentScreenVariant(this.state.fixedWrapperRef.offsetWidth)
      return
    }
    if (this.state.inlineWrapperRef) {
      this.setCurrentScreenVariant(this.state.inlineWrapperRef.offsetWidth)
    }
  }

  loadStories(page) {
    const currPlaylistRequestOptions = this.state.playlistRequestOptions
    let playlistRequestOptions = {}

    if (this.props.playlist === "publication") {
      playlistRequestOptions = {
        publication: this.props.publication,
        page,
      }
    } else if (this.props.continueWithPublicationPlaylist && (page > 1)) {
      playlistRequestOptions = {
        publication: this.props.publication,
        page: (currPlaylistRequestOptions && currPlaylistRequestOptions.url) ? 1 : page,
      }
    } else {
      playlistRequestOptions = {
        url: (this.props.playlist === "story") ? window.location.href : this.props.playlist,
      }
    }

    this.props.api
      .getPlaylist(playlistRequestOptions)
      .then((data) => {
        const { stories, publisher } = data

        if (this.props.autoplay) {
          // this.props.analytics.track("autostart")
        }

        this.setState({
          tracklist: stories,
          loaded: true,
          currentTrackIdx: 0,
          trackPos: 0,
          loadingNewPage: false,
          publisher,
          playlistRequestOptions,
          page,
          duration: stories[0].audio.duration || 0,
          didSessionStart: true,
        })
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("SpokenLayer player is unable to load episodes")
        this.setState({ loadingNewPage: false, tracklist: [] })
      })
  }

  play = () => {
    if (!this.state.loaded || this.state.playState === "playing") {
      return
    }

    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("play", {
      storyId: currentTrack.id,
    })

    this.setState({
      playState: "playing",
    })
  }

  pause = () => {
    if (!this.state.loaded || this.state.playState === "paused") {
      return
    }

    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("pause", {
      storyId: currentTrack.id,
    })

    this.setState({
      playState: "paused",
    })
  }

  trackHeartbeat = (newTrackPos) => {
    const currentTrack = this.getCurrentTrack()
    const posSeconds = Math.floor(newTrackPos)

    if (posSeconds === 5) {
      this.props.analytics.trackStoryEvent("heartbeat", {
        storyId: currentTrack.id,
        secondsListened: 5,
      })
    }

    if (posSeconds > 0 && posSeconds % 10 === 0) {
      this.props.analytics.trackStoryEvent("heartbeat", {
        storyId: currentTrack.id,
        secondsListened: posSeconds,
      })
    }

    if (posSeconds === Math.floor(this.state.duration) - 5) {
      this.props.analytics.trackStoryEvent("heartbeat", {
        storyId: currentTrack.id,
        secondsLeft: 5,
      })
    }
  }

  updateTrackPos = () => {
    if (!this.player.current) {
      return requestAnimationFrame(this.updateTrackPos)
    }

    const newTrackPos = Number(this.player.current.seek()) || 0

    if (this.state.trackPos !== newTrackPos) {
      if (Math.floor(this.state.trackPos) !== Math.floor(newTrackPos)) {
        this.trackHeartbeat(newTrackPos)
        this.receiver.emit("timeupdate", {
          seconds: newTrackPos,
          duration: this.state.duration,
        })
      }
      this.setState({
        trackPos: newTrackPos,
      })
    }

    return requestAnimationFrame(this.updateTrackPos)
  }

  handlePlayPauseClick = () => {
    if (!this.state.loaded) {
      return
    }

    const currentTrack = this.getCurrentTrack()
    const event = this.state.playState === "playing" ? "pause" : "play"
    const newState = this.state.playState === "playing" ? "paused" : "playing"

    this.props.analytics.trackStoryEvent(
      event,
      { storyId: currentTrack.id },
    )

    this.setState({
      playState: newState,
    })
  }

  // TODO: this can probably be removed
  handleStopClick = () => {
    if (!this.state.loaded) {
      return
    }
    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("stop", {
      storyId: currentTrack.id,
    })
    this.setState({
      playState: "stopped",
      trackPos: 0,
    })
  }

  handleRewindClick = () => {
    if (!this.state.loaded) {
      return
    }
    this.handleProgressSeek(0)
  }

  handleSkipClick = () => {
    if (!this.state.loaded) {
      return
    }

    const { currentTrackIdx, tracklist } = this.state

    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("next", {
      storyId: currentTrack.id,
    })

    // set state to stopped so we know to fire an audioStart on next play
    const nextPlayState = this.state.playState === "paused" ? "stopped" : this.state.playState
    const nextTrackIdx = (currentTrackIdx || 0) + 1

    if (nextTrackIdx >= tracklist.length) {
      this.setState(
        {
          playState: nextPlayState,
        },
        () => { this.loadStories(this.state.page + 1) }
      )
    } else {
      this.setState({
        currentTrackIdx: nextTrackIdx,
        trackPos: 0,
        playState: nextPlayState,
        duration: this.state.tracklist[nextTrackIdx].audio.duration || 0,
      })
    }
  }

  handleTrackEnd = () => {
    const { currentTrackIdx, tracklist } = this.state

    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("end", {
      storyId: currentTrack.id,
    })

    if (this.receiver) {
      this.receiver.emit("ended")
    }

    const nextTrackIdx = (currentTrackIdx || 0) + 1

    if (nextTrackIdx >= tracklist.length) {
      this.setState(
        {
          playState: "loading",
        },
        () => { this.loadStories(this.state.page + 1) }
      )
    } else {
      this.setState({
        currentTrackIdx: nextTrackIdx,
        trackPos: 0,
      })
    }
  }

  handleProgressSeek = (progressPercentage) => {
    if (!this.player.current || !this.state.loaded || progressPercentage > 100) {
      return
    }
    const newTrackPos = Math.floor(this.state.duration / 100 * progressPercentage)
    this.player.current.seek(newTrackPos || 0)
    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("rewind", {
      storyId: currentTrack.id,
      secondsListened: Math.floor(newTrackPos || 0),
    })
  }

  handleLoad = () => {
    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("start", {
      storyId: currentTrack.id,
    })
    this.setState({
      duration: this.player.current.duration(),
      playState: this.state.playState === "loading"
        ? "playing"
        : this.state.playState,
    })
  }

  handleShareClick = () => {
    this.setState({
      showShareOptions: !this.state.showShareOptions,
    })
  }

  handleShareClose = () => {
    if (this.state.showShareOptions) {
      this.setState({
        showShareOptions: false,
      })
    }
  }

  handleWrapperRef = (type) => (el) => {
    if (!el) {
      return
    }

    if (el.offsetWidth === 0) {
      requestAnimationFrame(() => this.handleWrapperRef(type)(el))
      return
    }

    if (type === "fixed") {
      this.setState({ fixedWrapperRef: el })
      this.setCurrentScreenVariant(el.offsetWidth)
      return
    }
    if (type === "inline") {
      this.setState({ inlineWrapperRef: el })
      this.setCurrentScreenVariant(el.offsetWidth)
    }
  }

  handleFixedWrapperRef = this.handleWrapperRef("fixed")
  handleInlineWrapperRef = this.handleWrapperRef("inline")

  handleShareButtonClicked = (sharingService) => () => {
    const currentTrack = this.getCurrentTrack()
    this.props.analytics.trackStoryEvent("share", {
      storyId: currentTrack.id,
      sharingService,
    })
  }

  renderInlinePlayer() {
    const {
      playState, duration, trackPos, collapsed, isTwoRowPlayer, showShareOptions, publisher, loadingNewPage, loaded, wrapperSize, tracklist,
    } = this.state

    const {
      theme, type, variant, share, shareTheme, hideWhenEmpty, continueWithPublicationPlaylist,
    } = this.props

    const currentTrack = this.getCurrentTrack()

    const isExtraMinimalScreen = wrapperSize === "extraMinimal"

    const isSkipAheadDisabled = tracklist.length <= 1 && !continueWithPublicationPlaylist

    const progress = (
      <Fragment>
        <Timer theme={theme} duration={trackPos} style={isTwoRowPlayer ? { marginRight: 10 } : { margin: "0 10px 0 30px" }} />
        <ProgressBar theme={theme} value={duration > 0 ? trackPos / duration * 100 : 0} onSeek={this.handleProgressSeek} />
        <Timer theme={theme} duration={duration} style={isTwoRowPlayer ? { marginLeft: 10 } : { margin: "0 0 0 10px" }} />
      </Fragment>
    )

    return (
      <InlineWrapper displayPlayer={currentTrack || !hideWhenEmpty} innerRef={this.handleInlineWrapperRef}>
        { !isExtraMinimalScreen && loaded && !currentTrack && <DisabledOverlay>No stories to Play</DisabledOverlay> }
        { !isExtraMinimalScreen && loaded && !loadingNewPage && isTwoRowPlayer && <TwoRowProgressWrapper>{progress}</TwoRowProgressWrapper>}
        { !isExtraMinimalScreen && (loadingNewPage || !loaded) && isTwoRowPlayer && <LoadingOverlay text='Loading stories' inline /> }

        <PlayerWrapper leftPad={variant.indexOf("large") === 0} onClick={this.handleShareClose}>
          {
            !isExtraMinimalScreen &&
            <ControlButton theme={theme} type='prev' onClick={this.handleRewindClick} />
          }
          {playState === "playing" && <ControlButton theme={theme} type='pause' onClick={this.handlePlayPauseClick} />}
          {playState !== "playing" && <ControlButton theme={theme} type='play' onClick={this.handlePlayPauseClick} />}
          {
            !isExtraMinimalScreen &&
            <ControlButton theme={theme} type='next' onClick={this.handleSkipClick} disabled={isSkipAheadDisabled} />
          }
          { !isExtraMinimalScreen && loaded && !loadingNewPage && !isTwoRowPlayer && progress}
          { !isExtraMinimalScreen && (loadingNewPage || !loaded) && !isTwoRowPlayer && <LoadingOverlay text='Loading stories' inline /> }
          { !isExtraMinimalScreen && !!share && <ControlButton theme={theme} type='share' onClick={this.handleShareClick} />}
          { !isExtraMinimalScreen && !!showShareOptions && (
            <ShareBox
              story={currentTrack}
              publisher={publisher}
              centerPosition={isTwoRowPlayer}
              iconsTheme={shareTheme}
              theme={theme}
              onShareButtonClicked={this.handleShareButtonClicked}
            />
          )}
          {playState !== "stopped" &&
            currentTrack && (
              <ReactHowler
                html5
                src={currentTrack.audio.src}
                playing={playState === "playing"}
                ref={this.player}
                onEnd={this.handleTrackEnd}
                onLoad={this.handleLoad}
              />
            )}
        </PlayerWrapper>
      </InlineWrapper>
    )
  }

  renderFixedPlayer() {
    const { wrapperSize } = this.state

    const {
      theme,
      variant,
      border,
      hideWhenEmpty,
    } = this.props

    const currentTrack = this.getCurrentTrack()

    let headerSize = "small"
    let mediumWrapperVariant = null

    const isSmallScreen = ["small", "minimal", "extraMinimal"].indexOf(wrapperSize) >= 0

    const isExtraMinimalScreen = wrapperSize === "extraMinimal"
    const isMinimalScreen = wrapperSize === "minimal"

    const isSmall = (currentTrack && variant === "small" && !isMinimalScreen && !isExtraMinimalScreen) ||
      (variant !== "minimal" && (["small", "minimal", "extraMinimal"].indexOf(wrapperSize) >= 0))
    const isLargeTop = currentTrack && variant === "largeTop" && !isSmallScreen
    const isLargeBottom = currentTrack && variant === "largeBottom" && !isSmallScreen
    const isMediumLeft = currentTrack && variant === "mediumLeft" && !isSmallScreen
    const isMediumRight = currentTrack && variant === "mediumRight" && !isSmallScreen
    const thumbnail = <Thumbnail src={currentTrack ? currentTrack.display.thumbnail.default : ""} />

    if (isMediumLeft || isMediumRight) {
      headerSize = "medium"
      mediumWrapperVariant = isMediumLeft ? "left" : "right"
    }

    if (isLargeTop || isLargeBottom) {
      headerSize = "large"
    }

    const header = <Header size={headerSize} theme={theme} currentTrack={currentTrack} wrapperSize={wrapperSize} />

    return (
      <PlayerFixedWrapper displayPlayer={currentTrack || !hideWhenEmpty} withBorder={border} theme={theme} variant={this.props.variant} innerRef={this.handleFixedWrapperRef}>
        {isSmall && header}
        {isLargeTop && (
          <LargeWrapper variant='top'>
            {thumbnail}
            {header}
          </LargeWrapper>
        )}
        <MediumWrapper>
          {isMediumLeft && thumbnail}
          <MediumPlayerWrapper variant={mediumWrapperVariant}>
            {(isMediumLeft || isMediumRight) && header}
            {this.renderInlinePlayer()}
          </MediumPlayerWrapper>
          {isMediumRight && thumbnail}
        </MediumWrapper>
        {isLargeBottom && (
          <LargeWrapper variant='bottom'>
            {thumbnail}
            {header}
          </LargeWrapper>
        )}
      </PlayerFixedWrapper>
    )
  }

  render() {
    return this.renderFixedPlayer()
  }
}

WebPlayer.propTypes = {
  api: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  autoplay: PropTypes.bool,
  publication: PropTypes.string.isRequired,
  playlist: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["fixed", "inline"]),
  theme: PropTypes.oneOf(["light", "dark"]),
  variant: PropTypes.oneOf(["minimal", "small", "mediumLeft", "mediumRight", "largeBottom", "largeTop"]),
  share: PropTypes.bool,
  border: PropTypes.bool,
  shareTheme: PropTypes.oneOf(["coloured", "inherit"]),
  onTrackChange: PropTypes.func,
  onAudioStart: PropTypes.func,
  hideWhenEmpty: PropTypes.bool,
  continueWithPublicationPlaylist: PropTypes.bool,
}

WebPlayer.defaultProps = {
  border: true,
  autoplay: false,
  playlist: "publication",
  type: "inline",
  theme: "light",
  variant: "minimal",
  shareTheme: "coloured",
  hideWhenEmpty: false,
  continueWithPublicationPlaylist: false,
}

export default WebPlayer



// WEBPACK FOOTER //
// ./src/components/organisms/WebPlayer/index.js